import { useQuery } from '@tanstack/react-query';
import { VIDEOS } from '../constants';
import type { Video } from '../types';
import { fetchVideoMetadata } from '../utils/wistia-api';

/**
 * Hook to fetch video metadata from Wistia oembed API
 * Fetches all videos in parallel and handles individual failures gracefully
 * Throws an error if all videos fail to load
 */
export function useVideoData() {
  return useQuery({
    queryKey: ['video-library', 'videos'],
    queryFn: async (): Promise<Video[]> => {
      const promises = VIDEOS.map(async (video) => {
        try {
          const response = await fetchVideoMetadata(video.id);
          return {
            id: video.id,
            thumbnailUrl: response.thumbnail_url,
            duration: Math.floor(response.duration),
            title: response.title,
            category: video.category,
          } satisfies Video;
        } catch {
          return null;
        }
      });

      const results = await Promise.all(promises);
      const videos = results.filter((v): v is Video => v !== null);

      // If all videos failed to load, throw an error to trigger error state
      if (videos.length === 0) {
        throw new Error('Failed to load any videos');
      }

      return videos.sort((a, b) =>
        a.category === b.category ? a.title.localeCompare(b.title) : a.category.localeCompare(b.category),
      );
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
}
